import {EventEmitter} from "@angular/core"
import {Box2, Vector2, Vector2Like, wrap} from "@cm/math"
import * as paper from "paper"
import {ToolMouseEvent} from "@common/helpers/canvas/canvas-base-toolbox-legacy/canvas-base-toolbox-item"
import {TilingCanvasToolboxItemBase} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/tiling-canvas-toolbox-item-base"
import {TilingCanvasBaseToolboxItem} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/tiling-canvas-toolbox-item"
import {CanvasToolboxItem} from "@common/helpers/canvas/canvas-toolbox/canvas-toolbox-item"

export class ShiftableToolboxItem extends TilingCanvasToolboxItemBase {
    readonly shiftInPixelsChanged = new EventEmitter<Vector2>()
    readonly beginShifting = new EventEmitter<void>()
    readonly endShifting = new EventEmitter<void>()

    // TODO reuse crosshair item
    private imageZeroLineGroup: paper.Group | null = null
    private imageZeroLineX: paper.Path | null = null
    private imageZeroLineY: paper.Path | null = null
    private _shiftInPixels = new Vector2(0, 0)
    private lastIntegrationMousePoint = new Vector2(0, 0)

    constructor(parent: TilingCanvasBaseToolboxItem) {
        super(parent)
        this.cursor = "move"
        this.callback.canvasBoundsChange.subscribe((value) => this.onCanvasBoundsChange(value))
        this.callback.viewChange.subscribe(() => this.onViewChange())
        this.selectedChange.subscribe(() => this.updateShowGuides())
        this.createZeroLineGuides()
    }

    override remove() {
        super.remove()
        this.imageZeroLineX?.remove()
        this.imageZeroLineY?.remove()
        this.imageZeroLineGroup?.remove()
    }

    override hitTest(point: Vector2Like): CanvasToolboxItem | null {
        if (this.selected && this.callback.isPointInImage(point)) {
            return this
        }
        return null
    }

    set showGuides(value: boolean) {
        this._showGuides = value
        this.updateShowGuides()
    }

    get showGuides(): boolean {
        return this._showGuides
    }

    set shiftInPixels(value: Vector2Like) {
        if (this._shiftInPixels.equals(value)) {
            return
        }
        this._shiftInPixels = Vector2.fromVector2Like(value)
        this.updateGuides()
        this.shiftInPixelsChanged.emit(this._shiftInPixels)
    }

    get shiftInPixels(): Vector2 {
        return this._shiftInPixels
    }

    get isUserInteracting(): boolean {
        return this._isUserInteracting
    }

    override onMouseDown(event: ToolMouseEvent): boolean {
        super.onMouseDown(event)
        this._isUserInteracting = true
        this.lastIntegrationMousePoint = event.point
        this.beginShifting.emit()
        return false
    }

    override onMouseUp(_event: ToolMouseEvent): boolean {
        super.onMouseUp(_event)
        if (this._isUserInteracting) {
            this._isUserInteracting = false
            this.endShifting.emit()
            return false
        }
        return true
    }

    override onMouseDrag(event: ToolMouseEvent): boolean {
        super.onMouseDrag(event)
        this.selected = true
        const delta = event.point.sub(this.lastIntegrationMousePoint).round()
        this.lastIntegrationMousePoint = this.lastIntegrationMousePoint.add(delta)
        this.shiftInPixels = this.shiftInPixels.add(Vector2.fromVector2Like(delta))
        return false
    }

    private createZeroLineGuides() {
        this.beginPaperCreation()
        if (this.imageZeroLineX) {
            this.imageZeroLineX.remove()
            this.imageZeroLineX = null
        }
        if (this.imageZeroLineY) {
            this.imageZeroLineY.remove()
            this.imageZeroLineY = null
        }
        if (this.imageZeroLineGroup) {
            this.imageZeroLineGroup.remove()
            this.imageZeroLineGroup = null
        }
        const canvasBounds = this.callback.canvasBounds
        this.imageZeroLineX = new paper.Path.Line(new paper.Point(0, canvasBounds.min.y), new paper.Point(0, canvasBounds.max.y))
        this.imageZeroLineX.strokeColor = new paper.Color("red")
        this.imageZeroLineY = new paper.Path.Line(new paper.Point(canvasBounds.min.x, 0), new paper.Point(canvasBounds.max.x, 0))
        this.imageZeroLineY.strokeColor = new paper.Color("red")
        this.imageZeroLineGroup = new paper.Group([this.imageZeroLineX, this.imageZeroLineY])
        this.updateShowGuides()
        this.updateGuides()
        this.updateZoomDependent()
    }

    private updateGuides() {
        const canvasBounds = this.callback.canvasBounds
        if (!canvasBounds.width || !canvasBounds.height) {
            return
        }
        if (this.imageZeroLineX) {
            this.imageZeroLineX.position.x = wrap(this._shiftInPixels.x - canvasBounds.min.x, canvasBounds.width) + canvasBounds.min.x
        }
        if (this.imageZeroLineY) {
            this.imageZeroLineY.position.y = wrap(this._shiftInPixels.y - canvasBounds.min.y, canvasBounds.height) + canvasBounds.min.y
        }
    }

    private updateZoomDependent() {
        const zoomLevel = this.callback.zoomLevel
        if (this.imageZeroLineX) {
            this.imageZeroLineX.strokeWidth = 1 / zoomLevel
        }
        if (this.imageZeroLineY) {
            this.imageZeroLineY.strokeWidth = 1 / zoomLevel
        }
    }

    private onCanvasBoundsChange(_value: Box2) {
        this.createZeroLineGuides()
    }

    private onViewChange() {
        this.updateZoomDependent()
    }

    private updateShowGuides() {
        if (this.imageZeroLineGroup) {
            this.imageZeroLineGroup.visible = this._showGuides && this.selected
        }
    }

    private _showGuides = true
    private _isUserInteracting = false
}
